import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/items")({
  component: ItemsPage,
});

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MoreHorizontal,
  Printer,
  X,
  Plus,
  Tag,
  Search,
  FileDown,
  FileUp,
  CheckSquare,
} from "lucide-react";
import { SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DashboardHeader } from "@/components/dashboard-header";
import { Input } from "@/components/ui/input";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import PageHeader from "@/components/reusable-components/header/page-header";
import { BarcodeScanControl } from "@/components/inventory/barcode/BarcodeScanControl";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchItems,
  useItemMutations,
  useFetchCategories,
  useFetchUnits,
  useFetchWarehouses,
  useGenerateBarcodes,
  useGenerateVariantBarcodes,
  fetchItemDeletionImpact,
  ALL_ACTIVE_PARAMS,
  type ItemOrdering,
} from "@/hooks/api/inventory/useCatalog";
import {
  fetchAssetItems,
  useFetchAssetItems,
  useGenerateAssetItemBarcodes,
  useAssetItemMutations,
  ALL_ASSET_ITEMS_PAGE_SIZE,
} from "@/hooks/api/inventory/useAssetItems";
import { inventoryKeys } from "@/hooks/api/inventory/keys";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import {
  useFetchCollections,
  useCollectionMutations,
  useFetchLabels,
  useItemAssignMutations,
  useFetchInspectionChecklists,
  useFetchInspectionFrequencies,
  useInspectionMutations,
} from "@/hooks/api/inventory/useCollectionsLabels";
import { useFetchTeams } from "@/hooks/api/crm/useTeams";
import { ExportItemsDialog } from "@/components/inventory/forms/ExportItemsDialog";
import { ItemTable } from "@/components/inventory/tables/ItemTable";
import { ItemFormSheet } from "@/components/inventory/forms/ItemFormSheet";
import { CollectionFormSheet } from "@/components/inventory/forms/CollectionFormSheet";
import { ItemDetailPanel } from "@/components/inventory/panels/ItemDetailPanel";
import { BarcodePreviewDialog } from "@/components/inventory/barcode/BarcodePreviewDialog";
import { combinationLabel } from "@/components/inventory/item-detail/VariantsTab";
import { RecordInspectionModal } from "@/components/inventory/forms/RecordInspectionModal";
import { InspectReturnModal } from "@/components/inventory/forms/InspectReturnModal";
import { AssignLabelsModal } from "@/components/inventory/forms/AssignLabelsModal";
import { AssignTeamsModal } from "@/components/inventory/forms/AssignTeamsModal";
import { MoveToFolderModal } from "@/components/inventory/forms/MoveToFolderModal";
import { SetFrequencyModal } from "@/components/inventory/forms/SetFrequencyModal";
import { ImportItemsDialog } from "@/components/inventory/forms/ImportItemsDialog";
import type {
  InventoryItem,
  InventoryItemPayload,
  BarcodeItem,
  AssignLabelsPayload,
  AssignTeamsPayload,
  RecordInspectionPayload,
  InspectReturnPayload,
  SetInspectionFrequencyPayload,
  Collection,
  CollectionPayload,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import {
  FORMAL_INSPECTION_ELIGIBLE_STATUSES,
  RETURN_INSPECTION_ELIGIBLE_STATUSES,
} from "@/lib/inventory";
import { FolderFilterSection } from "@/components/inventory/shared/FolderFilterSection";

const MAX_BULK_BARCODES = 100;
const MAX_BULK_LABELS = 100;
const LABEL_ASSIGN_BATCH_SIZE = 5;

function ItemsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  // ItemViewSet's required_permission_map falls every action besides
  // list/retrieve/*_stock/*_lookup/export to its "*" catch-all, so create,
  // edit, delete, inspect, assign-labels/teams, move-to-folder,
  // set-frequency, barcode generation and import all share this one code —
  // as does CollectionViewSet's write_permission for folders.
  const canManage = can(capabilities, "inventory.item.manage");
  // ItemViewSet.export is its own map entry, deliberately not item.manage.
  const canExport = can(capabilities, "inventory.report.view");
  // AssetItemViewSet.bulk_inspect_return is a write on asset *items*, not on
  // Item — its write_permission is inventory.rental.manage, distinct from
  // the item.manage that gates everything else on this page.
  const canManageRental = can(capabilities, "inventory.rental.manage");
  const categoriesQuery = useFetchCategories(ALL_ACTIVE_PARAMS);
  const categories = categoriesQuery.data?.results ?? [];
  const unitsQuery = useFetchUnits(ALL_ACTIVE_PARAMS);
  const units = unitsQuery.data?.results ?? [];
  const collectionsQuery = useFetchCollections(ALL_ACTIVE_PARAMS);
  const collections = collectionsQuery.data?.results ?? [];
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const labelsQuery = useFetchLabels(ALL_ACTIVE_PARAMS);
  const labels = labelsQuery.data?.results ?? [];
  const teamsQuery = useFetchTeams({
    is_active: true,
    ordering: "name",
    page_size: 100,
  });
  const teams = teamsQuery.data?.results ?? [];
  const checklistsQuery = useFetchInspectionChecklists(ALL_ACTIVE_PARAMS);
  const checklists = checklistsQuery.data?.results ?? [];
  const frequenciesQuery = useFetchInspectionFrequencies({
    ordering: "name",
    page_size: 100,
  });
  const frequencies = frequenciesQuery.data?.results ?? [];
  const { createItem, updateItem, deleteItem } = useItemMutations();
  const { createCollection, updateCollection, deleteCollection } =
    useCollectionMutations();
  const { assignLabels, assignTeams } = useItemAssignMutations();
  const { recordInspection, setInspectionFrequency } = useInspectionMutations();
  const { bulkInspectReturn } = useAssetItemMutations();
  const generateBarcodes = useGenerateBarcodes();
  const generateVariantBarcodes = useGenerateVariantBarcodes();
  const generateAssetItemBarcodes = useGenerateAssetItemBarcodes();
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState<"all" | "selected">("all");
  const queryClient = useQueryClient();

  // ── Pagination & filter state ─────────────────────────────────────────────
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [selectedCollection, setSelectedCollection] = useState<number | null>(
    null
  );
  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    undefined
  );
  const [safetyFilter, setSafetyFilter] = useState<string | undefined>(
    undefined
  );
  const [trackingFilter, setTrackingFilter] = useState<string | undefined>(
    undefined
  );
  const [activeFilter, setActiveFilter] = useState<
    "true" | "false" | undefined
  >(undefined);
  const [baseUnitFilter, setBaseUnitFilter] = useState<string | undefined>(
    undefined
  );
  const [labelFilter, setLabelFilter] = useState<string | undefined>(undefined);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        sku: SortDirection;
        tracking_mode: SortDirection;
        is_active: SortDirection;
        _latest_overall_status: SortDirection;
        _latest_inspection_date: SortDirection;
        manufacture_date: SortDirection;
        created_at: SortDirection;
        updated_at: SortDirection;
      },
      ItemOrdering
    >({
      name: null,
      sku: null,
      tracking_mode: null,
      is_active: null,
      _latest_overall_status: null,
      _latest_inspection_date: null,
      manufacture_date: null,
      created_at: null,
      updated_at: null,
    });

  // ── Layout toggles ────────────────────────────────────────────────────────
  const [showFolders, setShowFolders] = useState(true);

  // ── Item form / detail state ──────────────────────────────────────────────
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // ── Collection form state ─────────────────────────────────────────────────
  const [isCollectionSheetOpen, setIsCollectionSheetOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(
    null
  );
  const [deleteCollectionTarget, setDeleteCollectionTarget] =
    useState<Collection | null>(null);

  // ── Secondary action state ────────────────────────────────────────────────
  const [inspectTarget, setInspectTarget] = useState<InventoryItem | null>(
    null
  );
  const inspectTargetIsSerialized =
    inspectTarget?.tracking_mode === "SERIALIZED";
  const inspectAssetItemsQuery = useFetchAssetItems(
    {
      item: inspectTarget?.id,
      // Deployed/in-transit units aren't physically at a warehouse to
      // inspect -- excluded here so the picker never offers one that
      // record_inspection's own precondition would just reject anyway.
      status_in: [...FORMAL_INSPECTION_ELIGIBLE_STATUSES],
      page_size: ALL_ASSET_ITEMS_PAGE_SIZE,
    },
    { enabled: inspectTargetIsSerialized }
  );
  const inspectAssetItems = inspectAssetItemsQuery.data?.results ?? [];
  const [returnInspectTarget, setReturnInspectTarget] =
    useState<InventoryItem | null>(null);
  const returnInspectAssetItemsQuery = useFetchAssetItems(
    {
      item: returnInspectTarget?.id,
      status_in: [...RETURN_INSPECTION_ELIGIBLE_STATUSES],
      page_size: ALL_ASSET_ITEMS_PAGE_SIZE,
    },
    { enabled: !!returnInspectTarget }
  );
  const returnInspectAssetItems =
    returnInspectAssetItemsQuery.data?.results ?? [];
  const [assignLabelsTarget, setAssignLabelsTarget] =
    useState<InventoryItem | null>(null);
  const [assignTeamsTarget, setAssignTeamsTarget] =
    useState<InventoryItem | null>(null);
  const [moveToFolderTarget, setMoveToFolderTarget] =
    useState<InventoryItem | null>(null);
  const [frequencyTarget, setFrequencyTarget] = useState<InventoryItem | null>(
    null
  );

  // ── Barcode / bulk-label state ────────────────────────────────────────────
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectionPurpose, setSelectionPurpose] = useState<
    "barcode" | "label" | "export"
  >("barcode");
  const [isBulkLabelOpen, setIsBulkLabelOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [previewItems, setPreviewItems] = useState<BarcodeItem[] | null>(null);
  const [previewMode, setPreviewMode] = useState<"single" | "bulk">("single");

  const itemsQuery = useFetchItems({
    page_size: pageSize,
    page,
    collection: selectedCollection ?? undefined,
    search: searchFilter || undefined,
    category: categoryFilter ? parseInt(categoryFilter) : undefined,
    base_unit: baseUnitFilter ? parseInt(baseUnitFilter) : undefined,
    labels: labelFilter ? parseInt(labelFilter) : undefined,
    warehouse: warehouseFilter ? parseInt(warehouseFilter) : undefined,
    tracking_mode: trackingFilter,
    is_active:
      activeFilter === "true"
        ? true
        : activeFilter === "false"
          ? false
          : undefined,
    safety_status: safetyFilter,
    ordering: getApiOrdering(),
  });
  const items = itemsQuery.data?.results ?? [];
  const count = itemsQuery.data?.count ?? 0;

  const isMutating =
    createItem.isPending ||
    updateItem.isPending ||
    deleteItem.isPending ||
    createCollection.isPending ||
    updateCollection.isPending ||
    deleteCollection.isPending ||
    assignLabels.isPending ||
    assignTeams.isPending ||
    recordInspection.isPending ||
    bulkInspectReturn.isPending ||
    setInspectionFrequency.isPending ||
    generateBarcodes.isPending;
  const isLoading = itemsQuery.isPending || isMutating;
  const isFetching = itemsQuery.isFetching || isMutating;

  const resetFilters = () => {
    setSearchFilter("");
    setCategoryFilter(undefined);
    setSafetyFilter(undefined);
    setTrackingFilter(undefined);
    setActiveFilter(undefined);
    setBaseUnitFilter(undefined);
    setLabelFilter(undefined);
    setWarehouseFilter(undefined);
    setOrdering({
      name: null,
      sku: null,
      tracking_mode: null,
      is_active: null,
      _latest_overall_status: null,
      _latest_inspection_date: null,
      manufacture_date: null,
      created_at: null,
      updated_at: null,
    });
    setPage(1);
  };

  const selectCollection = (id: number | null) => {
    setSelectedCollection(id);
    setPage(1);
  };

  const openExport = (scope: "all" | "selected") => {
    setExportScope(scope);
    setIsExportOpen(true);
  };

  // ── Item handlers ─────────────────────────────────────────────────────────
  const handleSubmit = async (data: InventoryItemPayload) => {
    if (editing) {
      await updateItem.mutateAsync({ id: editing.id, payload: data });
      toast.success("Item updated");
    } else {
      const created = await createItem.mutateAsync(data);
      toast.success("Item created");
      setIsSheetOpen(false);
      return created;
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteItem.mutateAsync({ id: deleteTarget.id });
      toast.success("Item deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // Fetched the moment a delete is requested so the confirm dialog can show
  // the full consequence (or blockage) BEFORE the destructive click.
  const deletionImpactQuery = useQuery({
    queryKey: ["item-deletion-impact", deleteTarget?.id],
    queryFn: () => fetchItemDeletionImpact(deleteTarget!.id),
    enabled: !!deleteTarget,
    meta: { errorMessage: "Couldn't check what deleting this item affects." },
  });
  const deletionImpact = deletionImpactQuery.data;
  const deletionBlocked =
    !!deletionImpact && Object.keys(deletionImpact.blockers).length > 0;
  const impactLabel = (key: string) => key.replace(/_/g, " ");
  const deleteDescription =
    !deleteTarget ? null : deletionImpactQuery.isPending ? (
      <span>Checking what deleting "{deleteTarget.name}" would affect…</span>
    ) : deletionBlocked ? (
      <div className="space-y-2">
        <p>
          "{deleteTarget.name}" can't be deleted — it appears on documents that
          must be kept:
        </p>
        <ul className="list-disc pl-5 space-y-0.5">
          {Object.entries(deletionImpact!.blockers).map(([kind, numbers]) => (
            <li key={kind}>
              <span className="capitalize">{impactLabel(kind)}</span>:{" "}
              <span className="font-mono">{numbers.join(", ")}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : deletionImpact && Object.keys(deletionImpact.cascades).length > 0 ? (
      <div className="space-y-2">
        <p>
          Deleting "{deleteTarget.name}" cannot be undone and will also delete:
        </p>
        <ul className="list-disc pl-5 space-y-0.5">
          {Object.entries(deletionImpact.cascades).map(([kind, count]) => (
            <li key={kind}>
              {count} <span>{impactLabel(kind)}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : (
      <span>Delete item "{deleteTarget.name}"? This cannot be undone.</span>
    );

  const handleRecordInspection = async (payload: RecordInspectionPayload) => {
    if (!inspectTarget) return;
    await recordInspection.mutateAsync({ itemId: inspectTarget.id, payload });
    toast.success("Inspection recorded");
    setInspectTarget(null);
  };

  const handleReturnInspect = async (
    payload: InspectReturnPayload,
    assetItemIds?: number[]
  ) => {
    if (!assetItemIds?.length) return;
    await bulkInspectReturn.mutateAsync({ assetItemIds, payload });
    toast.success(
      assetItemIds.length > 1
        ? `Return Inspection recorded for ${assetItemIds.length} units`
        : "Return Inspection recorded"
    );
    setReturnInspectTarget(null);
  };

  const handleSetFrequency = async (data: SetInspectionFrequencyPayload) => {
    if (!frequencyTarget) return;
    await setInspectionFrequency.mutateAsync({
      itemId: frequencyTarget.id,
      payload: data,
    });
    toast.success(
      data.inspection_frequency_id || data.inspection_due_date
        ? "Inspection schedule set"
        : "Inspection schedule removed"
    );
    setFrequencyTarget(null);
  };

  const handleAssignLabels = async (payload: AssignLabelsPayload) => {
    if (!assignLabelsTarget) return;
    await assignLabels.mutateAsync({ itemId: assignLabelsTarget.id, payload });
    toast.success("Labels updated");
    setAssignLabelsTarget(null);
  };

  const handleAssignTeams = async (payload: AssignTeamsPayload) => {
    if (!assignTeamsTarget) return;
    await assignTeams.mutateAsync({ itemId: assignTeamsTarget.id, payload });
    toast.success("Teams updated");
    setAssignTeamsTarget(null);
  };

  const handleMoveToFolder = async (collectionId: number | null) => {
    if (!moveToFolderTarget) return;
    await updateItem.mutateAsync({
      id: moveToFolderTarget.id,
      payload: { collection_id: collectionId },
    });
    toast.success(
      collectionId ? "Item moved to folder" : "Item removed from folder"
    );
    setMoveToFolderTarget(null);
  };

  // ── Collection handlers ───────────────────────────────────────────────────
  const handleCollectionSubmit = async (data: CollectionPayload) => {
    if (editingCollection) {
      await updateCollection.mutateAsync({
        id: editingCollection.id,
        payload: data,
      });
      toast.success("Collection updated");
    } else {
      await createCollection.mutateAsync(data);
      toast.success("Collection created");
    }
    setIsCollectionSheetOpen(false);
    setEditingCollection(null);
  };

  const handleDeleteCollectionConfirm = async () => {
    if (!deleteCollectionTarget) return;
    try {
      await deleteCollection.mutateAsync({ id: deleteCollectionTarget.id });
      toast.success("Collection deleted");
      setDeleteCollectionTarget(null);
      if (selectedCollection === deleteCollectionTarget.id)
        selectCollection(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  // ── Barcode handlers ──────────────────────────────────────────────────────
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected =
      items.length > 0 && items.every((i) => selectedIds.has(i.id));
    setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  };

  const startSelection = (purpose: "barcode" | "label" | "export") => {
    setSelectionPurpose(purpose);
    setSelectionMode(true);
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkAssignLabels = async (payload: AssignLabelsPayload) => {
    if (selectedIds.size > MAX_BULK_LABELS) {
      toast.error(
        `You can assign labels to at most ${MAX_BULK_LABELS} items at a time.`
      );
      return;
    }
    const ids = Array.from(selectedIds);
    for (let i = 0; i < ids.length; i += LABEL_ASSIGN_BATCH_SIZE) {
      await Promise.all(
        ids
          .slice(i, i + LABEL_ASSIGN_BATCH_SIZE)
          .map((id) => assignLabels.mutateAsync({ itemId: id, payload }))
      );
    }
    toast.success(
      `Labels assigned to ${selectedIds.size} item${selectedIds.size > 1 ? "s" : ""}`
    );
    setIsBulkLabelOpen(false);
    cancelSelection();
  };

  // Items with real (non-default) variants are stocked/scanned at the variant
  // level as well as the item level — export the item's own barcode *and*
  // one per variant, and let the user pick which of those to actually print.
  // The auto-generated "default" variant just mirrors the item's own
  // SKU/barcode, so it's excluded to avoid a redundant duplicate sticker.
  const realVariantsOf = (item: InventoryItem) =>
    (item.variants ?? []).filter((v) => !v.is_default);

  const variantToBarcodeRow = (
    item: InventoryItem,
    variant: (typeof item.variants)[number]
  ): BarcodeItem => ({
    item_id: -variant.id,
    sku: variant.sku,
    barcode_value: variant.barcode_value,
    name: `${item.name} — ${combinationLabel(variant)}`,
    barcode: variant.barcode,
    barcode_width_mm: variant.barcode_width_mm,
    barcode_height_mm: variant.barcode_height_mm,
    kind: "variant",
  });

  const baseRowOf = (item: InventoryItem): BarcodeItem => ({
    item_id: item.id,
    sku: item.sku,
    barcode_value: item.barcode_value,
    name: item.name,
    barcode: item.barcode,
    barcode_width_mm: item.barcode_width_mm,
    barcode_height_mm: item.barcode_height_mm,
    kind: "item",
  });

  // A serialized item's units are scanned/tracked individually — surface
  // each unit's own barcode alongside the item's/variants' the same way, so
  // "everything you can scan for this item" is always one merged list.
  const fetchAssetRowsFor = async (
    item: InventoryItem
  ): Promise<BarcodeItem[]> => {
    if (item.tracking_mode !== "SERIALIZED") return [];
    const assets = await queryClient.ensureQueryData({
      queryKey: inventoryKeys.assetItems.list({
        item: item.id,
        page_size: ALL_ASSET_ITEMS_PAGE_SIZE,
      }),
      queryFn: () =>
        fetchAssetItems({
          item: item.id,
          page_size: ALL_ASSET_ITEMS_PAGE_SIZE,
        }),
    });
    return assets.results.map((a) => ({
      item_id: -a.id,
      sku: a.asset_identifier,
      barcode_value: a.barcode_value,
      name: `${item.name} — ${a.asset_identifier}`,
      barcode: a.barcode,
      barcode_width_mm: a.barcode_width_mm,
      barcode_height_mm: a.barcode_height_mm,
      kind: "asset",
    }));
  };

  // variantToBarcodeRow's `item_id: -variant.id` doubles as the key back to
  // the variant once its barcode comes back from generate-barcodes — no
  // separate lookup needed. `kind` disambiguates from asset rows, which reuse
  // the same negative-id trick against a different id space.
  const fillMissingVariantBarcodes = async (
    rows: BarcodeItem[]
  ): Promise<BarcodeItem[]> => {
    const missingVariantIds = rows
      .filter((r) => !r.barcode && r.kind === "variant")
      .map((r) => -r.item_id);
    if (missingVariantIds.length === 0) return rows;
    const fetched = await generateVariantBarcodes.mutateAsync({
      ids: missingVariantIds,
    });
    const byVariantId = new Map(fetched.map((f) => [f.variant_id, f]));
    return rows.map((r) => {
      const f = r.kind === "variant" ? byVariantId.get(-r.item_id) : undefined;
      return f
        ? {
            ...r,
            barcode: f.barcode,
            barcode_value: f.barcode_value,
            barcode_width_mm: f.barcode_width_mm,
            barcode_height_mm: f.barcode_height_mm,
          }
        : r;
    });
  };

  const fillMissingAssetBarcodes = async (
    rows: BarcodeItem[]
  ): Promise<BarcodeItem[]> => {
    const missingAssetIds = rows
      .filter((r) => !r.barcode && r.kind === "asset")
      .map((r) => -r.item_id);
    if (missingAssetIds.length === 0) return rows;
    const fetched = await generateAssetItemBarcodes.mutateAsync({
      ids: missingAssetIds,
    });
    const byAssetId = new Map(fetched.map((f) => [f.asset_item_id, f]));
    return rows.map((r) => {
      const f = r.kind === "asset" ? byAssetId.get(-r.item_id) : undefined;
      return f
        ? {
            ...r,
            barcode: f.barcode,
            barcode_value: f.barcode_value,
            barcode_width_mm: f.barcode_width_mm,
            barcode_height_mm: f.barcode_height_mm,
          }
        : r;
    });
  };

  const openSingleBarcode = async (item: InventoryItem) => {
    try {
      const variantRows = realVariantsOf(item).map((v) =>
        variantToBarcodeRow(item, v)
      );
      const assetRows = await fetchAssetRowsFor(item);
      const baseRow = item.barcode
        ? baseRowOf(item)
        : ((await generateBarcodes.mutateAsync({ ids: [item.id] }))[0] ?? null);
      let rows = baseRow
        ? [baseRow, ...variantRows, ...assetRows]
        : [...variantRows, ...assetRows];
      rows = await fillMissingVariantBarcodes(rows);
      rows = await fillMissingAssetBarcodes(rows);
      setPreviewMode(rows.length > 1 ? "bulk" : "single");
      setPreviewItems(rows);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const openBulkBarcodes = async () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size > MAX_BULK_BARCODES) {
      toast.error(
        `You can export at most ${MAX_BULK_BARCODES} barcodes at a time.`
      );
      return;
    }
    const ids = Array.from(selectedIds);
    const itemsById = new Map(items.map((i) => [i.id, i]));
    const rowsByItemId = new Map<number, BarcodeItem[]>();
    const missing: number[] = [];

    for (const id of ids) {
      const it = itemsById.get(id);
      if (!it) continue;
      const variantRows = realVariantsOf(it).map((v) =>
        variantToBarcodeRow(it, v)
      );
      if (it.barcode) {
        rowsByItemId.set(id, [baseRowOf(it), ...variantRows]);
      } else {
        // Stash the variant rows now; the fetched base row is prepended once generated below.
        rowsByItemId.set(id, variantRows);
        missing.push(id);
      }
    }

    try {
      if (missing.length > 0) {
        const fetched = await generateBarcodes.mutateAsync({ ids: missing });
        for (const row of fetched) {
          rowsByItemId.set(row.item_id, [
            row,
            ...(rowsByItemId.get(row.item_id) ?? []),
          ]);
        }
      }
      // Serialized items' asset units join the same merged list — fetched
      // per item (cheap no-op for bulk-tracked items, see fetchAssetRowsFor).
      const assetRowsById = await Promise.all(
        ids.map(async (id) => {
          const it = itemsById.get(id);
          return [id, it ? await fetchAssetRowsFor(it) : []] as const;
        })
      );
      for (const [id, assetRows] of assetRowsById) {
        if (assetRows.length > 0) {
          rowsByItemId.set(id, [...(rowsByItemId.get(id) ?? []), ...assetRows]);
        }
      }
      let merged = ids.flatMap((id) => rowsByItemId.get(id) ?? []);
      if (merged.length > MAX_BULK_BARCODES) {
        toast.error(
          `That would print ${merged.length} barcodes (items with variants/asset units add more) — select fewer items, up to ${MAX_BULK_BARCODES} total.`
        );
        return;
      }
      merged = await fillMissingVariantBarcodes(merged);
      merged = await fillMissingAssetBarcodes(merged);
      setPreviewMode("bulk");
      setPreviewItems(merged);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const folderTitle = selectedCollection
    ? (collections.find((c) => c.id === selectedCollection)?.name ?? "Folder")
    : "Items outside of folders";

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        {/* ── Page header ──────────────────────────────────────────────────── */}
        {!selectionMode ? (
          <PageHeader
            title="Items"
            description="Manage your items, folders, and stock catalog."
            onToggleFilter={() => setShowFilters((v) => !v)}
            onAdd={() => {
              setEditing(null);
              setIsViewOnly(false);
              setIsSheetOpen(true);
            }}
            addLabel="Item"
            isLoading={isFetching}
          >
            {(canManage || canExport) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isFetching}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canManage && (
                    <>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => startSelection("barcode")}
                      >
                        <Printer className="mr-2 h-4 w-4" /> Select to Export
                        Barcodes
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => startSelection("label")}
                      >
                        <Tag className="mr-2 h-4 w-4" /> Select to Assign Labels
                      </DropdownMenuItem>
                    </>
                  )}
                  {canExport && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => startSelection("export")}
                    >
                      <CheckSquare className="mr-2 h-4 w-4" /> Select to Export
                      Excel
                    </DropdownMenuItem>
                  )}
                  {canExport && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => openExport("all")}
                    >
                      <FileDown className="mr-2 h-4 w-4" /> Export to Excel
                    </DropdownMenuItem>
                  )}
                  {canManage && (
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => setIsImportOpen(true)}
                    >
                      <FileUp className="mr-2 h-4 w-4" /> Import from Excel
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingCollection(null);
                  setIsCollectionSheetOpen(true);
                }}
                disabled={isFetching}
              >
                <Plus className="h-4 w-4" />
                Folder
              </Button>
            )}
          </PageHeader>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-xl font-semibold">All inventory</h1>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={cancelSelection}
                disabled={isFetching}
              >
                <X className="h-4 w-4" /> Cancel
              </Button>
              {selectionPurpose === "barcode" ? (
                <Button
                  size="sm"
                  onClick={openBulkBarcodes}
                  disabled={isFetching || selectedIds.size === 0}
                >
                  <Printer className="h-4 w-4" />
                  Export Barcode
                  {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                </Button>
              ) : selectionPurpose === "label" ? (
                <Button
                  size="sm"
                  onClick={() => setIsBulkLabelOpen(true)}
                  disabled={isFetching || selectedIds.size === 0}
                >
                  <Tag className="h-4 w-4" />
                  Assign Labels
                  {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => openExport("selected")}
                  disabled={isFetching || selectedIds.size === 0}
                >
                  <FileDown className="h-4 w-4" />
                  Export
                  {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                </Button>
              )}
            </div>
          </div>
        )}

        <FolderFilterSection
          collections={collections}
          isPending={collectionsQuery.isPending}
          selectedCollection={selectedCollection}
          onSelectCollection={selectCollection}
          showFolders={showFolders}
          onToggleShowFolders={() => setShowFolders((v) => !v)}
          onEditCollection={
            canManage
              ? (col) => {
                  setEditingCollection(col);
                  setIsCollectionSheetOpen(true);
                }
              : undefined
          }
          onDeleteCollection={
            canManage ? (col) => setDeleteCollectionTarget(col) : undefined
          }
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or SKU..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8"
            />
          </div>
          <SelectFilter
            placeholder="Category"
            options={categories.map((c) => ({
              label: c.name,
              value: String(c.id),
            }))}
            value={categoryFilter}
            onChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Warehouse"
            options={warehouses.map((w) => ({
              label: w.name,
              value: String(w.id),
            }))}
            value={warehouseFilter}
            onChange={(v) => {
              setWarehouseFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Safety Status"
            options={[
              { label: "Safe", value: "SAFE" },
              { label: "Not Safe", value: "NOT_SAFE" },
              { label: "Monitoring", value: "MONITORING" },
              { label: "Quarantined", value: "QUARANTINED" },
              { label: "Overdue", value: "OVERDUE" },
              { label: "Inspection Required", value: "INSPECTION_REQUIRED" },
            ]}
            value={safetyFilter}
            onChange={(v) => {
              setSafetyFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Tracking"
            options={[
              { label: "Bulk", value: "BULK" },
              { label: "Serialized", value: "SERIALIZED" },
            ]}
            value={trackingFilter}
            onChange={(v) => {
              setTrackingFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]}
            value={activeFilter}
            onChange={(v) => {
              setActiveFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Base Unit"
            options={units.map((u) => ({ label: u.name, value: String(u.id) }))}
            value={baseUnitFilter}
            onChange={(v) => {
              setBaseUnitFilter(v);
              setPage(1);
            }}
          />
          <SelectFilter
            placeholder="Label"
            options={labels.map((l) => ({
              label: l.name,
              value: String(l.id),
            }))}
            value={labelFilter}
            onChange={(v) => {
              setLabelFilter(v);
              setPage(1);
            }}
          />
          <BarcodeScanControl
            onScan={(code) => {
              setSearchFilter(code.trim());
              setPage(1);
            }}
            dialogDescription="Point the camera at an item or variant barcode to search for it."
          />
        </FiltersPanel>

        {/* ── Items table section ───────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">{folderTitle}</h2>
              {selectedCollection && (
                <Badge
                  variant="muted"
                  className="cursor-pointer text-xs"
                  onClick={() => selectCollection(null)}
                >
                  Clear ×
                </Badge>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <ItemTable
              items={items}
              isLoading={isLoading}
              ordering={ordering}
              onOrderingChange={toggleOrdering}
              onNameClick={(item) => {
                navigate({
                  to: "/admin/inventory/items/$id",
                  params: { id: String(item.id) },
                });
              }}
              onView={(i) => {
                setSelectedItem(i);
                setIsPanelOpen(true);
              }}
              onEdit={(i) => {
                setEditing(i);
                setIsViewOnly(false);
                setIsSheetOpen(true);
              }}
              onInspect={(i) => setInspectTarget(i)}
              onReturnInspect={(i) => setReturnInspectTarget(i)}
              onMoveToFolder={(i) => setMoveToFolderTarget(i)}
              onSetFrequency={(i) => setFrequencyTarget(i)}
              onDelete={(i) => setDeleteTarget(i)}
              onExportBarcode={openSingleBarcode}
              onAssignLabels={(i) => setAssignLabelsTarget(i)}
              onAssignTeams={(i) => setAssignTeamsTarget(i)}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              canManage={canManage}
              canManageRental={canManageRental}
            />
            <PaginationComponent
              count={count}
              page={page}
              onPageChange={(p) => {
                setPage(p);
              }}
              isLoading={isFetching}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </div>
        </div>
      </div>

      {/* ── Panels & dialogs ─────────────────────────────────────────────────── */}
      <ItemDetailPanel
        item={selectedItem}
        open={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        onEdit={(i) => {
          setIsPanelOpen(false);
          setEditing(i);
          setIsViewOnly(false);
          setIsSheetOpen(true);
        }}
        onInspect={(i) => {
          setIsPanelOpen(false);
          setInspectTarget(i);
        }}
      />

      <ItemFormSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        onSubmit={handleSubmit}
        editing={editing}
        categories={categories}
        units={units}
        collections={collections}
        warehouses={warehouses}
        frequencies={frequencies}
        isLoading={createItem.isPending || updateItem.isPending}
        isViewOnly={isViewOnly}
      />

      <CollectionFormSheet
        isOpen={isCollectionSheetOpen}
        onClose={() => {
          setIsCollectionSheetOpen(false);
          setEditingCollection(null);
        }}
        onSubmit={handleCollectionSubmit}
        editing={editingCollection}
        isLoading={createCollection.isPending || updateCollection.isPending}
      />

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Item"
        description={deleteDescription}
        itemName={deleteTarget?.name}
        isLoading={deleteItem.isPending}
        confirmDisabled={deletionImpactQuery.isPending || deletionBlocked}
        destructive
      />

      <DeleteConfirmDialog
        isOpen={!!deleteCollectionTarget}
        onClose={() => setDeleteCollectionTarget(null)}
        onConfirm={handleDeleteCollectionConfirm}
        title="Delete Collection"
        description={`Delete collection "${deleteCollectionTarget?.name}"? This cannot be undone.`}
        itemName={deleteCollectionTarget?.name}
        isLoading={deleteCollection.isPending}
        destructive
      />

      <BarcodePreviewDialog
        isOpen={!!previewItems}
        onClose={() => setPreviewItems(null)}
        items={previewItems ?? []}
        mode={previewMode}
      />

      <RecordInspectionModal
        isOpen={!!inspectTarget}
        onClose={() => setInspectTarget(null)}
        onSubmit={handleRecordInspection}
        item={inspectTarget}
        checklists={checklists}
        isLoading={recordInspection.isPending}
        assetItems={inspectTargetIsSerialized ? inspectAssetItems : undefined}
        requireAssetSelection={inspectTargetIsSerialized}
      />

      <InspectReturnModal
        isOpen={!!returnInspectTarget}
        onClose={() => setReturnInspectTarget(null)}
        onSubmit={handleReturnInspect}
        assetItem={null}
        item={returnInspectTarget}
        assetItems={returnInspectAssetItems}
        requireAssetSelection
        checklists={checklists}
        isLoading={bulkInspectReturn.isPending}
      />

      <AssignLabelsModal
        isOpen={!!assignLabelsTarget}
        onClose={() => setAssignLabelsTarget(null)}
        onSubmit={handleAssignLabels}
        entity={assignLabelsTarget}
        subtitle={
          assignLabelsTarget
            ? `${assignLabelsTarget.name} — ${assignLabelsTarget.sku}`
            : undefined
        }
        allLabels={labels}
        isLoading={assignLabels.isPending}
      />

      <AssignLabelsModal
        isOpen={isBulkLabelOpen}
        onClose={() => setIsBulkLabelOpen(false)}
        onSubmit={handleBulkAssignLabels}
        entity={null}
        bulkCount={selectedIds.size}
        allLabels={labels}
        isLoading={assignLabels.isPending}
      />

      <AssignTeamsModal
        isOpen={!!assignTeamsTarget}
        onClose={() => setAssignTeamsTarget(null)}
        onSubmit={handleAssignTeams}
        item={assignTeamsTarget}
        allTeams={teams}
        isLoading={assignTeams.isPending}
      />

      <SetFrequencyModal
        isOpen={!!frequencyTarget}
        onClose={() => setFrequencyTarget(null)}
        onSubmit={handleSetFrequency}
        title="Set Inspection Schedule"
        description={frequencyTarget?.name ?? ""}
        currentFrequencyId={frequencyTarget?.inspection_frequency?.id ?? null}
        currentDueDate={frequencyTarget?.inspection_due_date ?? null}
        frequencies={frequencies}
        isLoading={setInspectionFrequency.isPending}
      />

      <MoveToFolderModal
        isOpen={!!moveToFolderTarget}
        onClose={() => setMoveToFolderTarget(null)}
        onSubmit={handleMoveToFolder}
        item={moveToFolderTarget}
        collections={collections}
        isLoading={updateItem.isPending}
      />

      <ImportItemsDialog
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />

      <ExportItemsDialog
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        warehouses={warehouses}
        categories={categories}
        labels={labels}
        collections={collections}
        units={units}
        selectedIds={selectedIds}
        onlySelected={exportScope === "selected"}
        initial={{
          search: searchFilter,
          category: categoryFilter,
          warehouse: warehouseFilter,
          label: labelFilter,
          collection: selectedCollection,
          baseUnit: baseUnitFilter,
          tracking: trackingFilter,
          safety: safetyFilter,
          active: activeFilter,
          ordering: getApiOrdering(),
        }}
      />
    </SidebarInset>
  );
}
